import React, { useEffect, useState } from 'react';

import Button from '@atlaskit/button';
import PremiumIcon from '@atlaskit/icon/glyph/premium';

import { FEATURE_FLAG } from '@qality/common-config';
import { User } from '@qality/common-model';
import { UserService } from '@qality/common-service';

import OnboardingPanel from '../OnboardingPanel/OboardingPanel';
import { onboardingPanelsData } from './OnboardingPlaylist.data';
import {
  OnboardingContentStyled,
  OnboardingHeaderButtonsWrapper,
  OnboardingHeaderStyled,
  OnboardingHeaderTextStyled,
  OnboardingHeaderTextWrapper,
  OnboardingWrapper,
} from './OnboardingPlaylist.styled';
import OnboardingPlaylistService, {
  ONBOARDING_PANEL_EXPANDED_PROPERTY_KEY,
  ONBOARDING_PANEL_VISIBILITY_PROPERTY_KEY,
} from './OnboardingPlaylistService';

export default function OnboardingPlaylist() {
  const [isOnboardingVisible, setIsOnboardingVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [currentUser, setCurrentUser] = useState<User>();

  const toggleVisibilityButtonText = isExpanded ? 'Hide' : 'Show more';

  useEffect(() => {
    if (FEATURE_FLAG.isPremium) {
      initializeShowOnboardingPlaylist();
    } else {
      setIsOnboardingVisible(false);
    }
  }, []);

  const initializeShowOnboardingPlaylist = async (): Promise<void> => {
    const user = await UserService.getCurrentUser();
    setCurrentUser(user);

    await initializeExpandedProperty(user);

    const shouldShowOnboarding =
      await OnboardingPlaylistService.shouldShowOnboardingPlaylist(user);

    setIsOnboardingVisible(shouldShowOnboarding);
  };

  const togglePanelVisibility = async () => {
    OnboardingPlaylistService.setUserProperty(
      currentUser,
      !isExpanded,
      ONBOARDING_PANEL_EXPANDED_PROPERTY_KEY
    );
    setIsExpanded(!isExpanded);
  };

  const initializeExpandedProperty = async (user): Promise<void> => {
    const isOnboardingExpanded: boolean =
      await OnboardingPlaylistService.getUserProperty(
        user,
        ONBOARDING_PANEL_EXPANDED_PROPERTY_KEY
      );

    setIsExpanded(isOnboardingExpanded);
  };

  const handleDontShowAgainClick = () => {
    OnboardingPlaylistService.setUserProperty(
      currentUser,
      false,
      ONBOARDING_PANEL_VISIBILITY_PROPERTY_KEY
    );
    setIsOnboardingVisible(false);
  };

  const getOnboardingPanels = (): Array<JSX.Element> => {
    return onboardingPanelsData.map((panelData) => {
      return (
        <OnboardingPanel
          key={panelData.title}
          title={panelData.title}
          description={panelData.description}
          appearance={panelData.appearance}
          icon={panelData.icon}
          iconLabel={panelData.iconLabel}
          redirectUrl={panelData.redirectUrl}
        />
      );
    });
  };

  return (
    <>
      {isOnboardingVisible && (
        <OnboardingWrapper>
          <OnboardingHeaderStyled>
            <OnboardingHeaderTextWrapper>
              <PremiumIcon label='onboarding-header-icon' size='medium' />
              <OnboardingHeaderTextStyled>
                Make your first steps in QAlity Plus as easy as possible
              </OnboardingHeaderTextStyled>
            </OnboardingHeaderTextWrapper>
            <OnboardingHeaderButtonsWrapper>
              {isExpanded && (
                <Button appearance='subtle' onClick={handleDontShowAgainClick}>
                  Don&apos;t show again
                </Button>
              )}
              <Button appearance='link' onClick={togglePanelVisibility}>
                {toggleVisibilityButtonText}
              </Button>
            </OnboardingHeaderButtonsWrapper>
          </OnboardingHeaderStyled>
          {isExpanded && (
            <OnboardingContentStyled>
              {getOnboardingPanels()}
            </OnboardingContentStyled>
          )}
        </OnboardingWrapper>
      )}
    </>
  );
}
